import {
  BASE_PATH,
  NAME_MAX_LENGTH,
  type Cell,
  type Duration,
  sanitizeName,
  type ErrorCode,
  type FoundInfo,
  type PlayerInfo,
  type RoomStatus,
  type SeriesInfo,
  type ServerMsg,
} from "../src/protocol";
import type { Lang } from "../src/languages";
import type { ThemeId } from "../src/themes";
import { BoardView } from "./board";
import { track } from "./analytics";
import { openDialog, type DialogHandle } from "./dialog";
import { h } from "./dom";
import { brandMark, siteFooter } from "./home";
import { copyIcon, exitIcon, shareIcon } from "./icons";
import { Connection, type ConnectionStatus } from "./net";
import { qrSvg } from "./qr";
import { outcome, rankPlayers, type RankingEntry } from "./ranking";
import { openResults } from "./results";
import type { ResultCard } from "./shareCard";
import { getName, getPlayerId, setName } from "./storage";
import { languageName, STRINGS } from "./strings";
import { uiLangSelect } from "./langPicker";
import { selectedTheme, themeSelect } from "./themePicker";
import { formatClock, throttle } from "./timing";

const CANONICAL_ORIGIN = "https://workbrawl.com";
/** ~12 drag messages per second at most (incoming WS messages are billed). */
const DRAG_INTERVAL_MS = 85;
const POP_ANIMATION_MS = 700;
const NEW_ROUND_NOTICE_MS = 3500;
/** The clock is green until this much time is left, then shifts gradually towards red… */
const CLOCK_WARN_MS = 30_000;
/** …which it reaches here: the last seconds are solid red and blink. */
const CLOCK_FINAL_MS = 10_000;
const CLOCK_GREEN_HUE = 135;
const CLOCK_REFRESH_MS = 250;
/** If no new board arrives after picking a theme (e.g. lost message), unlock the picker. */
const THEME_CHANGE_TIMEOUT_MS = 4000;
const TOAST_MS = 2500;

interface GameState {
  you: string;
  round: number;
  size: number;
  words: string[];
  theme: ThemeId;
  /** Language of the room's words (may differ from this player's UI language). */
  lang: Lang;
  /** Identifies the current board, to tell a fresh deal from a re-sent state. */
  gridKey: string;
  status: RoomStatus;
  /** Round length in seconds; null in rooms without a time limit. */
  duration: Duration | null;
  /** Local time at which the countdown or the round ends; null when no clock is running. */
  deadline: number | null;
  found: Map<string, FoundInfo>;
  players: Map<string, PlayerInfo>;
  /** Where the room is in its series of rounds. */
  series: SeriesInfo;
}

export function renderGame(root: HTMLElement, code: string): void {
  const name = getName();
  if (sanitizeName(name) === null) {
    renderNamePrompt(root, code);
    return;
  }
  new GameScreen(root, code, sanitizeName(name)!);
}

function renderNamePrompt(root: HTMLElement, code: string): void {
  const input = h("input", {
    id: "name",
    type: "text",
    autocomplete: "nickname",
    maxlength: NAME_MAX_LENGTH,
    placeholder: STRINGS.namePlaceholder,
    required: true,
  });
  const error = h("p", { class: "form-error", role: "alert" });
  const form = h(
    "form",
    { class: "card name-prompt", novalidate: true },
    h("h2", {}, STRINGS.namePromptTitle),
    h("p", { class: "muted" }, STRINGS.namePromptBody),
    h("label", { for: "name" }, STRINGS.nameLabel),
    input,
    h("button", { type: "submit", class: "btn btn-primary" }, STRINGS.enterRoom),
    error,
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const name = sanitizeName(input.value);
    if (name === null) {
      error.textContent = STRINGS.nameInvalid;
      input.focus();
      return;
    }
    setName(name);
    new GameScreen(root, code, name);
  });
  root.replaceChildren(
    h("main", { class: "center-screen" }, h("h1", { class: "brand brand-large" }, brandMark()), form),
    siteFooter(),
  );
  input.focus();
}

class GameScreen {
  private state: GameState | null = null;
  private fatal = false;
  private online = false;
  private results: DialogHandle | null = null;
  private noticeTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly connection: Connection;
  private readonly board: BoardView;
  private dragSent = false;
  private readonly sendDrag = throttle((start: Cell, end: Cell) => {
    this.dragSent = this.connection.send({ type: "drag", start, end }) || this.dragSent;
  }, DRAG_INTERVAL_MS);

  /** Floating message pinned to the top of the viewport (connection state, notices). */
  private readonly statusEl = h("div", { class: "toast", role: "status", "aria-live": "polite", hidden: true });
  private readonly themeEl = h("h2", { class: "theme" });
  private readonly wordsEl = h("ul", { class: "word-list" });
  private readonly progressEl = h("span", { class: "muted" });
  private readonly playersEl = h("ol", { class: "player-list" });
  private readonly clockEl = h("strong", { class: "round-clock", title: STRINGS.timeLeft, "aria-label": STRINGS.timeLeft });
  /** Covers the board before a timed round starts: Start button, then the 3-2-1. */
  private readonly coverEl = h("div", { class: "board-cover", hidden: true });
  private clockTimer: ReturnType<typeof setInterval> | undefined;
  private readonly mainEl: HTMLElement;

  constructor(
    private readonly root: HTMLElement,
    private readonly code: string,
    name: string,
  ) {
    this.board = new BoardView({
      onSelect: (start, end) => {
        if (!this.connection.send({ type: "select", start, end })) this.board.rejectPending();
      },
      onDrag: (start, end) => this.sendDrag(start, end),
      onDragEnd: () => {
        this.sendDrag.cancel();
        if (this.dragSent) this.connection.send({ type: "drag_end" });
        this.dragSent = false;
      },
    });
    this.board.el.append(this.coverEl);

    this.mainEl = h(
      "main",
      { class: "game" },
      this.themeEl,
      h("section", { class: "board-area" }, this.board.el),
      h(
        "aside",
        { class: "panel" },
        h(
          "section",
          { class: "card" },
          h("h3", { class: "panel-title" }, STRINGS.words, " ", this.progressEl),
          this.wordsEl,
        ),
        h("section", { class: "card" }, h("h3", { class: "panel-title" }, STRINGS.scoreboard), this.playersEl),
        this.inviteCard(),
      ),
    );

    root.replaceChildren(this.header(), this.statusEl, this.mainEl, siteFooter());

    this.connection = new Connection(Connection.roomUrl(code, getPlayerId(), name), {
      onMessage: (msg) => this.onMessage(msg),
      onStatus: (status) => this.onStatus(status),
    });
    this.connection.connect();
  }

  private header(): HTMLElement {
    const exitButton = h(
      "button",
      { type: "button", class: "btn btn-small btn-exit", "aria-haspopup": "dialog", "aria-label": STRINGS.exit },
      exitIcon(),
      // Hidden on narrow screens, where the button is icon-only.
      h("span", { class: "btn-label" }, STRINGS.exit),
    );
    exitButton.addEventListener("click", () => this.confirmExit());

    return h(
      "header",
      { class: "topbar" },
      h("a", { href: `${BASE_PATH}/`, class: "brand" }, brandMark()),
      h(
        "div",
        { class: "room-info" },
        h("span", { class: "muted room-label" }, STRINGS.room),
        h("strong", { class: "room-code" }, this.code),
        uiLangSelect(true),
        exitButton,
      ),
    );
  }

  private confirmExit(): void {
    const cancel = h("button", { type: "button", class: "btn" }, STRINGS.cancel);
    const leave = h("button", { type: "button", class: "btn btn-danger" }, exitIcon(), STRINGS.exit);
    const dialog = openDialog(
      "confirm",
      h("h2", { class: "dialog-title" }, STRINGS.exitTitle),
      h("p", { class: "muted" }, STRINGS.exitBody),
      h("div", { class: "dialog-actions" }, cancel, leave),
    );
    cancel.addEventListener("click", () => dialog.close());
    leave.addEventListener("click", () => {
      // Suppress the "connection lost" message while we leave on purpose.
      this.fatal = true;
      this.connection.stop();
      location.assign(`${BASE_PATH}/`);
    });
    cancel.focus();
  }

  /** Copies the invite link; reports through a floating message unless `onDone` handles it. */
  private async copyInvite(onDone?: (ok: boolean) => void): Promise<void> {
    const url = inviteUrl(this.code);
    const ok = await copyText(url);
    if (onDone) onDone(ok);
    else this.showNotice(ok ? STRINGS.linkCopiedToast : `${STRINGS.copyFailed} ${url}`);
  }

  /** Invite card under the scoreboard: title with share/copy actions on the right. */
  private inviteCard(): HTMLElement {
    const share = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.share, "aria-label": STRINGS.share, "aria-haspopup": "dialog" },
      shareIcon(),
    );
    share.addEventListener("click", () => this.openInvite());
    const copy = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.copyLink, "aria-label": STRINGS.copyLink },
      copyIcon(),
    );
    copy.addEventListener("click", () => this.copyInvite());

    return h(
      "section",
      { class: "card invite-card" },
      h(
        "div",
        { class: "invite-head" },
        h("h3", { class: "panel-title" }, STRINGS.qrTitle),
        h("div", { class: "icon-actions" }, share, copy),
      ),
    );
  }

  /** Modal with a large QR, the link, copy and (where supported) the native share sheet. */
  private openInvite(): void {
    const url = inviteUrl(this.code);
    const copy = h("button", { type: "button", class: "btn" }, copyIcon(), STRINGS.copyLink);
    copy.addEventListener("click", () =>
      this.copyInvite((ok) => {
        copy.replaceChildren(copyIcon(), ok ? STRINGS.linkCopied : STRINGS.copyFailedShort);
        setTimeout(() => copy.replaceChildren(copyIcon(), STRINGS.copyLink), 1500);
      }),
    );
    const close = h("button", { type: "button", class: "btn" }, STRINGS.close);

    const actions: HTMLElement[] = [];
    if (typeof navigator.share === "function") {
      const native = h("button", { type: "button", class: "btn btn-primary" }, shareIcon(), STRINGS.shareVia);
      native.addEventListener("click", () => {
        navigator.share({ title: STRINGS.appName, text: STRINGS.shareText, url }).catch(() => {
          // User dismissed the share sheet: nothing to do.
        });
      });
      actions.push(native);
    }
    actions.push(copy, close);

    const dialog = openDialog(
      "invite",
      h("h2", { class: "dialog-title" }, STRINGS.qrTitle),
      h("div", { class: "qr-box qr-box-large" }, qrSvg(url, STRINGS.qrLabel(this.code))),
      h("p", { class: "invite-url" }, url),
      h("div", { class: "dialog-actions" }, ...actions),
    );
    close.addEventListener("click", () => dialog.close());
  }

  /**
   * "Ronda N · Temática: X". Before the first find (while connected) X is a button-like
   * picker that switches the theme as soon as an option is chosen; afterwards the same
   * control stays in place, locked, until the rematch.
   */
  private renderTheme(): void {
    const state = this.state;
    if (!state) return;
    const prefix = h(
      "span",
      { class: "muted theme-prefix" },
      state.series.length > 1 ? STRINGS.roundOf(state.series.index, state.series.length) : STRINGS.round(state.round),
      " · ",
      // Dropped on narrow screens so round, picker and language tag fit on one line.
      h("span", { class: "theme-word" }, `${STRINGS.theme}: `),
    );
    // Which language the words are in: matters when it differs from the UI language.
    const langTag = h(
      "span",
      { class: "tag lang-tag", title: STRINGS.wordsIn(languageName(state.lang)) },
      state.lang.toUpperCase(),
    );
    // Timed rooms: open until Start. Untimed: open until the first word is found.
    const editable =
      this.online &&
      (state.duration ? state.status === "waiting" : state.status === "playing" && state.found.size === 0);
    // The clock sits at the end of this line in timed rooms (always there, so nothing jumps).
    const clock = state.duration ? this.clockEl : null;

    // The same control is always rendered (enabled or locked) so the line keeps its exact
    // size: swapping it for plain text made the board jump when the first word was found.
    const select = themeSelect("theme-change", state.theme);
    select.classList.add("select-inline");
    select.setAttribute("aria-label", STRINGS.themeLabel);
    if (!editable) {
      select.disabled = true;
      select.classList.add("is-locked");
      select.title = STRINGS.themeLocked;
      this.themeEl.replaceChildren(prefix, select, langTag, ...(clock ? [clock] : []));
      return;
    }
    select.addEventListener("change", () => {
      if (!this.connection.send({ type: "change_theme", theme: selectedTheme(select) })) {
        this.renderTheme();
        return;
      }
      // Locked until the new board (a fresh state) re-renders the picker.
      select.disabled = true;
      select.setAttribute("aria-busy", "true");
      setTimeout(() => {
        if (select.isConnected) this.renderTheme();
      }, THEME_CHANGE_TIMEOUT_MS);
    });
    this.themeEl.replaceChildren(prefix, select, langTag, ...(clock ? [clock] : []));
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.type) {
      case "state": {
        const previousRound = this.state?.round;
        const previousGrid = this.state?.gridKey;
        const previousTheme = this.state?.theme;
        const previousStatus = this.state?.status;
        const gridKey = msg.grid.map((row) => row.join("")).join("|");
        this.state = {
          you: msg.you,
          round: msg.round,
          size: msg.size,
          words: msg.words,
          theme: msg.theme,
          lang: msg.lang,
          gridKey,
          status: msg.status,
          duration: msg.duration,
          deadline: msg.remainingMs === null ? null : Date.now() + msg.remainingMs,
          found: new Map(msg.found.map((f) => [f.word, f])),
          players: new Map(msg.players.map((p) => [p.id, p])),
          series: msg.series,
        };
        // Animate the letters when a new board replaces the current one (rematch or theme change,
        // including re-picking the same theme); a re-sent state after reconnecting is not new.
        // One per person per round: this is what "how much is each title used" is measured by.
        if (msg.status === "playing" && previousStatus !== "playing") {
          track("round_started", { people: msg.players.length, duration: msg.duration ?? 0 });
        }
        const newBoard = previousGrid !== undefined && gridKey !== previousGrid;
        this.board.setGrid(msg.grid, newBoard);
        this.renderAll();
        if (msg.status === "finished") {
          if (!this.results) this.showResults(rankPlayers(this.state.players.values()));
        } else {
          this.closeResults();
          const themeName = STRINGS.themes[msg.theme];
          if (previousRound !== undefined && msg.round > previousRound) {
            this.showNotice(STRINGS.newRound(themeName), NEW_ROUND_NOTICE_MS);
          } else if (
            previousTheme !== undefined &&
            // A new deal while playing (untimed rooms), or another theme picked while waiting.
            // Not the board appearing when a timed round starts.
            (previousTheme !== msg.theme || (newBoard && previousStatus === "playing"))
          ) {
            this.showNotice(STRINGS.themeChanged(themeName), NEW_ROUND_NOTICE_MS);
          }
        }
        break;
      }
      case "player_joined":
      case "player_updated":
        this.state?.players.set(msg.player.id, msg.player);
        this.renderPlayers();
        this.renderLines();
        this.renderPhase();
        break;
      case "player_left": {
        const player = this.state?.players.get(msg.id);
        if (player) player.online = false;
        this.board.setPreview(msg.id, null);
        this.renderPlayers();
        this.renderPhase();
        break;
      }
      case "found": {
        if (!this.state) break;
        this.state.found.set(msg.word, { word: msg.word, start: msg.start, end: msg.end, by: msg.by });
        const player = this.state.players.get(msg.by);
        if (player) player.score = msg.score;
        const mine = msg.by === this.state.you;
        if (mine) {
          this.board.acceptPending();
          navigator.vibrate?.(25);
        }
        this.board.setPreview(msg.by, null);
        this.renderWords(msg.word);
        this.renderPlayers(msg.by);
        if (this.state.found.size === 1) this.renderTheme();
        this.renderLines({ word: msg.word, mine });
        break;
      }
      case "miss":
        this.board.rejectPending();
        navigator.vibrate?.(40);
        break;
      case "drag": {
        const color = this.state?.players.get(msg.by)?.color;
        if (color && msg.by !== this.state?.you) {
          this.board.setPreview(msg.by, { start: msg.start, end: msg.end, color });
        }
        break;
      }
      case "drag_end":
        this.board.setPreview(msg.by, null);
        break;
      case "game_over":
        if (!this.state) break;
        track("round_finished", {
          theme: this.state.theme,
          word_language: this.state.lang,
          people: this.state.players.size,
          rounds: msg.series.length,
          series_round: msg.series.index,
        });
        this.state.series = msg.series;
        this.state.status = "finished";
        this.state.deadline = null;
        this.renderPhase();
        this.updateBoardMode();
        this.showFinishedStatus();
        this.showResults(msg.ranking);
        break;
      case "error":
        this.onError(msg.code, msg.message);
        break;
      case "pong":
        break;
    }
  }

  private onStatus(status: ConnectionStatus): void {
    this.online = status === "open";
    this.updateBoardMode();
    this.renderTheme();
    if (!this.online) this.board.clearPreviews();
    if (this.fatal) return;
    if (status === "connecting") this.showStatus(STRINGS.statusConnecting);
    else if (status === "reconnecting") this.showStatus(STRINGS.statusReconnecting, true);
    else if (status === "stopped") this.showStatus(STRINGS.statusStopped, true);
    else if (this.state?.status === "finished") this.showFinishedStatus();
    else this.showStatus(null);
  }

  /** Offline dims the board; a finished round only locks it so the final board stays readable. */
  private updateBoardMode(): void {
    if (!this.online) this.board.setMode("offline");
    else if (this.state?.status !== "playing") this.board.setMode("locked");
    else this.board.setMode("play");
  }

  /**
   * Timed rooms: the cover over the board (Start button, then 3-2-1) and the clock.
   * The server sends no ticks, so the room can hibernate: the clock runs locally from
   * the deadline in the last state.
   */
  private renderPhase(): void {
    const state = this.state;
    clearInterval(this.clockTimer);
    this.clockTimer = undefined;
    if (!state || !state.duration) {
      this.coverEl.hidden = true;
      return;
    }

    if (state.status === "waiting") {
      const online = [...state.players.values()].filter((p) => p.online).length;
      const start = h("button", { type: "button", class: "btn btn-primary" }, STRINGS.startRound);
      start.addEventListener("click", () => this.connection.send({ type: "start" }));
      this.coverEl.replaceChildren(
        h("h2", {}, STRINGS.waitingTitle),
        h("p", {}, STRINGS.waitingBody(online)),
        start,
        h("p", { class: "muted" }, STRINGS.waitingHint),
      );
      this.coverEl.hidden = false;
      this.setClock(state.duration * 1000, false);
    } else if (state.status === "countdown") {
      const number = h("strong", { class: "countdown" });
      const update = () => {
        const next = String(Math.max(1, Math.ceil(((state.deadline ?? 0) - Date.now()) / 1000)));
        if (number.textContent === next) return;
        number.textContent = next;
        // Restart the zoom-out for each new number (reading offsetWidth forces a reflow).
        number.classList.remove("countdown-pop");
        void number.offsetWidth;
        number.classList.add("countdown-pop");
      };
      update();
      this.clockTimer = setInterval(update, 100);
      this.coverEl.replaceChildren(h("p", {}, STRINGS.getReady), number);
      this.coverEl.hidden = false;
      this.setClock(state.duration * 1000, false);
    } else if (state.status === "playing" && state.deadline !== null) {
      this.coverEl.hidden = true;
      const deadline = state.deadline;
      const update = () => this.setClock(deadline - Date.now(), true);
      update();
      this.clockTimer = setInterval(update, CLOCK_REFRESH_MS);
    } else {
      this.coverEl.hidden = true;
      this.setClock(0, false);
    }
  }

  /** Green with time to spare, through yellow and orange, red and blinking at the end. */
  private setClock(ms: number, running: boolean): void {
    this.clockEl.textContent = formatClock(ms);
    this.clockEl.classList.toggle("clock-running", running);
    this.clockEl.classList.toggle("clock-final", running && ms <= CLOCK_FINAL_MS);
    const left = Math.min(1, Math.max(0, (ms - CLOCK_FINAL_MS) / (CLOCK_WARN_MS - CLOCK_FINAL_MS)));
    this.clockEl.style.setProperty("--clock-hue", String(Math.round(CLOCK_GREEN_HUE * left)));
  }

  private showResults(ranking: RankingEntry[]): void {
    const state = this.state;
    if (!state) return;
    this.closeResults();
    const handle = openResults({
      ranking,
      series: state.series,
      players: state.players,
      you: state.you,
      card: this.resultCard(ranking),
      onRematch: (theme) => {
        track("rematch", { theme });
        this.connection.send({ type: "rematch", theme });
      },
    });
    handle.el.addEventListener("close", () => {
      if (this.results === handle) this.results = null;
    });
    this.results = handle;
  }

  /**
   * What "Share result" draws: the podium, worded for people who weren't in the room. After
   * the last round of a series, the series totals instead of that round's points.
   */
  private resultCard(ranking: RankingEntry[]): ResultCard {
    const state = this.state!;
    const { series } = state;
    const final = series.length > 1 && series.index >= series.length;
    const entries = final
      ? series.standings.map((s) => ({ id: s.id, name: s.name, score: s.total }))
      : ranking;
    const result = outcome(entries, "");
    const headline =
      result.kind === "winner"
        ? final
          ? STRINGS.seriesWinner(result.name)
          : STRINGS.winner(result.name)
        : final
          ? STRINGS.seriesTie
          : STRINGS.tie;
    return {
      title: "WordsBrawl",
      slug: "wordsbrawl",
      accent: "#3b5bdb",
      headline,
      podium: entries.slice(0, 3).map((entry) => ({
        name: entry.name,
        score: STRINGS.points(entry.score),
        color: state.players.get(entry.id)?.color ?? "#3b5bdb",
      })),
      people: entries.length,
      minutes: state.duration ? Math.round(state.duration / 60) : 0,
      rounds: final ? series.length : 1,
    };
  }

  private closeResults(): void {
    this.results?.close();
    this.results = null;
  }

  private showFinishedStatus(): void {
    // A timed round can end with words still hidden: say why it ended.
    const allFound = !this.state || this.state.found.size >= this.state.words.length;
    this.showStatus(allFound ? STRINGS.gameFinished : STRINGS.timeUp, false, {
      label: STRINGS.viewResults,
      onClick: () => {
        if (this.state) this.showResults(rankPlayers(this.state.players.values()));
      },
    });
  }

  /** Transient, non-warning message (e.g. a new round started, link copied). */
  private showNotice(text: string, durationMs = TOAST_MS): void {
    this.showStatus(text);
    this.noticeTimer = setTimeout(() => {
      if (this.statusEl.textContent === text) this.restoreStatus();
    }, durationMs);
  }

  /** After a transient notice, fall back to the persistent message for the current state. */
  private restoreStatus(): void {
    if (this.fatal) return;
    if (!this.online) this.showStatus(STRINGS.statusReconnecting, true);
    else if (this.state?.status === "finished") this.showFinishedStatus();
    else this.showStatus(null);
  }

  private onError(code: ErrorCode, message: string): void {
    if (code === "BAD_MESSAGE") {
      console.warn("[WordsBrawl]", message);
      return;
    }
    if (code === "THEME_LOCKED") {
      this.showNotice(STRINGS.themeLocked);
      this.renderTheme();
      return;
    }
    this.fatal = true;
    this.closeResults();
    this.connection.stop();
    const text =
      code === "ROOM_NOT_FOUND"
        ? STRINGS.errorRoomNotFound
        : code === "ROOM_FULL"
          ? STRINGS.errorRoomFull
          : STRINGS.errorGeneric;
    this.statusEl.hidden = true;
    this.mainEl.replaceChildren(
      h(
        "section",
        { class: "card center-card" },
        h("p", {}, text),
        h("a", { href: `${BASE_PATH}/`, class: "btn btn-primary" }, STRINGS.backHome),
      ),
    );
  }

  private showStatus(
    text: string | null,
    warning = false,
    action?: { label: string; onClick(): void },
  ): void {
    clearTimeout(this.noticeTimer);
    const wasHidden = this.statusEl.hidden;
    const changed = this.statusEl.firstChild?.textContent !== text;
    this.statusEl.hidden = text === null;
    this.statusEl.classList.toggle("toast-warning", warning);
    if (text !== null && (wasHidden || changed)) {
      // Replay the slide-in so each new message is noticed.
      this.statusEl.classList.remove("toast-in");
      void this.statusEl.offsetWidth;
      this.statusEl.classList.add("toast-in");
    }
    const children: Node[] = [h("span", { class: "toast-text" }, text ?? "")];
    if (action) {
      const button = h("button", { type: "button", class: "btn btn-small" }, action.label);
      button.addEventListener("click", action.onClick);
      children.push(button);
    }
    this.statusEl.replaceChildren(...children);
  }

  private renderAll(): void {
    if (!this.state) return;
    this.renderTheme();
    const me = this.state.players.get(this.state.you);
    if (me) this.board.setPlayerColor(me.color);
    this.renderWords();
    this.renderPlayers();
    this.renderLines();
    this.renderPhase();
    this.updateBoardMode();
    if (this.state.status === "finished") this.showFinishedStatus();
    else if (this.online) this.showStatus(null);
  }

  private renderWords(justFound?: string): void {
    const state = this.state;
    if (!state) return;
    this.progressEl.textContent = STRINGS.wordsProgress(state.found.size, state.words.length);
    this.wordsEl.replaceChildren(
      ...state.words.map((word) => {
        const found = state.found.get(word);
        const li = h("li", { class: found ? "word word-found" : "word" }, word);
        if (word === justFound) flash(li, "word-pop");
        if (found) {
          const color = state.players.get(found.by)?.color;
          if (color) li.style.setProperty("--by", color);
          const finder = state.players.get(found.by)?.name;
          if (finder) li.title = finder;
        }
        return li;
      }),
    );
  }

  private renderPlayers(scored?: string): void {
    const state = this.state;
    if (!state) return;
    const players = [...state.players.values()].sort(
      (a, b) => b.score - a.score || a.name.localeCompare(b.name),
    );
    this.playersEl.replaceChildren(
      ...players.map((p) => {
        const li = h(
          "li",
          { class: p.online ? "player" : "player player-offline" },
          h("span", { class: "avatar", "aria-hidden": "true" }, initial(p.name)),
          h(
            "span",
            { class: "player-name" },
            p.name,
            p.id === state.you ? h("span", { class: "tag" }, STRINGS.you) : null,
            p.online ? null : h("span", { class: "tag tag-muted" }, STRINGS.offline),
          ),
          h("span", { class: "player-score", title: STRINGS.points(p.score) }, String(p.score)),
        );
        if (p.id === scored) flash(li, "player-scored");
        li.style.setProperty("--player", p.color);
        return li;
      }),
    );
  }

  private renderLines(animate?: { word: string; mine: boolean }): void {
    const state = this.state;
    if (!state) return;
    this.board.setFound(
      [...state.found.values()].map((f) => ({
        word: f.word,
        start: f.start,
        end: f.end,
        color: state.players.get(f.by)?.color ?? "currentColor",
      })),
      animate,
    );
  }
}

/** Adds a one-shot animation class (CSS handles prefers-reduced-motion). */
function flash(el: HTMLElement, className: string): void {
  el.classList.add(className);
  setTimeout(() => el.classList.remove(className), POP_ANIMATION_MS);
}

function initial(name: string): string {
  const [first = "?"] = [...name.trim()];
  return first.toUpperCase();
}

/**
 * Invite links use the canonical domain, except when running locally or on
 * *.workers.dev, where the canonical link would point to a different deployment.
 */
function inviteUrl(code: string): string {
  const host = location.hostname;
  const isLocal =
    host === "localhost" ||
    /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) ||
    host.endsWith(".workers.dev");
  return `${isLocal ? location.origin : CANONICAL_ORIGIN}${BASE_PATH}/r/${code}`;
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  const area = h("textarea", { readonly: true, class: "visually-hidden" });
  area.value = text;
  document.body.append(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}
